/**
 * https://journal.tinkoff.ru/neresident-calc/
 * https://www.tinkoff.ru/invest/education/courses/a74fa808-e93c-4c0b-a7ef-ac546ed13486/afe6c71d-fa3b-42f5-9d82-82390e88df9a/
 * https://www.nalog.gov.ru/rn77/fl/interest/dual_nationality/status/
 *
 * TODO Статус на определенную дату в будущем
 * TODO ДОбавить секцию: "Когда статус изменится?"
 *
 * TODO Учесть: Помните, период нахождения в РФ не прерывается на периоды выезда для краткосрочного (менее 6 месяцев) лечения или обучения, а также для исполнения трудовых или иных обязанностей, связанных с выполнением работ (оказанием услуг) на морских месторождениях углеводородного сырья.
 *
 * TODO Ключевики "Сертификат налогового резидента"
 *  https://ru.uniwide.biz/articles/sertifikat-nalogovogo-rezidentstva-podrobnyy-obzor/
 *  справка +о налоговом резидентстве рф 500

 */
import { LoaderFunctionArgs, MetaFunction } from "react-router";
import { useEffect, useState } from "react";

import Button from "~/components/Button";
import FormGroup from "~/components/FormGroup";
import DatePicker from "~/components/forms/DatePicker";
import DateRangePicker from "~/components/forms/DateRangePicker";
import H2 from "~/components/H2";
import Icon from "~/components/Icon";
import Label from "~/components/Label";
import AdditionalInfo from "~/components/tool-page/AdditionalInfo";
import AdditionalInfoSection from "~/components/tool-page/AdditionalInfoSection";
import Result from "~/components/tool-page/Result";
import ToolHeader from "~/components/tool-page/ToolHeader";
import { makeTitle } from "~/helpers/pageTitle";
import { createWebAppSchema } from "~/helpers/schemaOrg";
import { getLocale } from "~/i18n";
import CalculatorLayout from "~/routes/layouts/components/CalculatorLayout";
import AdditionalContentLayout from "~/routes/layouts/components/AdditionalContentLayout";
import ToolHeaderNew from "~/components/tool-page/ToolHeaderNew";


const CATEGORY = "taxes";
const TKEY = "calculators.taxes.residency";
const ROUTE_ENDING = "residency";


export const loader = async ({ request }: LoaderFunctionArgs) => {
  const locale = getLocale(request.url);
  if (locale !== "ru") {
    throw new Response(null, {
      status: 404
    });
  }

  const breadcrumbs = [
    { title: `catalog.categories.${CATEGORY}`, to: `${CATEGORY}` },
    { title: `${TKEY}.title`, to: `${CATEGORY}/${ROUTE_ENDING}` }
  ];

  return {
    meta: {
      title: "Определение статуса налогового резидента РФ, калькулятор",
      breadcrumbs
    }
  };
};

export const meta: MetaFunction<typeof loader> = ({ data }) => [
  { title: makeTitle(data?.meta?.title) },
  {
    name: "description",
    content: "Калькулятор определяет ваш статус налогового резидента по количеству дней проведенных за пределами Российской Федерации"
  },
  createWebAppSchema("ru", "Определение статуса налогового резидента РФ, калькулятор", [])
];


interface Trip {
  from: Date | null;
  till: Date | null;
}

export default function Index() {
  const [checkDate, setCheckDate] = useState<Date>(new Date());
  const [trips, setTrips] = useState<Trip[]>([{ from: null, till: null }]);
  const [countOfDaysOutsideRussia, setCountOfDaysOutsideRussia] = useState(0);
  const [hasValidTrips, setHasValidTrips] = useState<boolean>(false);

  const recalculate = () => {
    console.log("Recalculating");
    let reallyHasValidTrips = false;

    let daysCount = 0;
    const checkDate = new Date();
    const twelveMonthsAgo = new Date();
    twelveMonthsAgo.setMonth(checkDate.getMonth() - 12);

    trips.forEach((trip: Trip) => {
      if (trip.from === null || trip.till === null) {
        return;
      }

      reallyHasValidTrips = true;

      // Don't count trips finished earlier than 12 months ago
      if (trip.till < twelveMonthsAgo) {
        return;
      }

      // If trip started earlier than 12 month ago, but finished later, then count only days after 12 months ago
      const startDate = new Date(
        Math.max(
          trip.from.getTime(),
          twelveMonthsAgo.getTime() - (24 * 60 * 60 * 1000) // minus 1 day in milliseconds
        )
      );

      daysCount += microsecondsToDays(trip.till.getTime() - startDate.getTime()) - 1;
    });

    setCountOfDaysOutsideRussia(daysCount >= 365 ? 365 : daysCount);
    setHasValidTrips(reallyHasValidTrips);
  };


  useEffect(() => {
    recalculate();
  }, [trips]);

  const addTrip = async () => {
    const updatedTrips = [...trips, { from: null, till: null }];
    await setTrips(updatedTrips);
  };

  const removeTrip = (index: number) => {
    const updatedTrips = [...trips];
    updatedTrips.splice(index, 1);
    setTrips(updatedTrips);
  };

  const updateTrip = (index: number, from: Date | null, till: Date | null) => {
    // I didn't figure out why, but if you do not use updater function here,
    // then trips will have incorrect value here
    setTrips(trips => {
      const updatedTrips = [...trips];
      updatedTrips[index].from = from;
      updatedTrips[index].till = till;

      return updatedTrips;
    });
  };

  const microsecondsToDays = (microseconds: number) => {
    return Math.ceil(microseconds / (1000 * 3600 * 24));
  };


  /**
   * Exclusive
   */
  function isDateWithinRange(date: Date, rangeStart: Date, rangeEnd: Date): boolean {
    if (rangeStart && date <= rangeStart) {
      return false;
    }

    if (rangeEnd && date >= rangeEnd) {
      return false;
    }

    return true;
  }

  function checkDateSelectable(date: Date): boolean {
    for (const trip of trips) {
      if (trip.from === null || trip.till === null) {
        continue;
      }

      if (isDateWithinRange(date, trip.from, trip.till)) {
        return false;
      }
    }

    return true;
  }

  return (
    <>
      <CalculatorLayout>
        <ToolHeaderNew
          name="Определение статуса налогового резидента РФ"
          description="Калькулятор поможет понять свой статус по количеству дней проведенных за пределами Российской Федерации за последние 12 месяцев"
        />

        <p>Статус налогового резидента РФ определяется по количеству дней, проведенных за границей за последние 12
          месяцев. Нужны даты отъезда и возвращения в Россию для всех поездок.</p>

        <p>Не все поездки влияют на статус. Если вы выезжали на лечение, обучение сроком до 6 месяцев, в поездки для
          работы на морских месторождениях нефти и газа, военной или государственной службы, то эти дни не
          учитываются, если вы сможете документально это подтвердить. Такие поездки добавлять в расчет не нужно.</p>

        <H2 className="mt-6 mb-4">На какую дату определять статус:</H2>

        <p className="mb-4">По-умолчанию стоит сегодняшняя дата. Однако, ФМС определяет статус налогового резидентства
          ежегодно 31 декабря. Это не значит, что с нового года дни, проведенные за рубежом, обнуляются.</p>

        <div className="max-w-[280px]">
          <FormGroup>
            <DatePicker
              name="check-date"
              placeholder="Укажите дату"
              value={checkDate}
              onChange={(date) => setCheckDate(date)}
            />
          </FormGroup>
        </div>

        <H2 className="mt-6 mb-4">Добавьте поездки за последние 12 месяцев:</H2>

        {trips.map((trip, index) => (
            <div key={index} className="w-full flex flex-row my-6 gap-6 items-end">
              <div className="w-2/3 max-w-[280px]">
                <FormGroup>
                  <Label
                    htmlFor={"trip-dates-" + index}
                    text="Даты отъезда и возвращения"
                  />
                  <DateRangePicker
                    name={"trip-dates-" + index}
                    placeholder="Укажите даты"
                    value={trip}
                    onChange={(datesRange) => updateTrip(index, datesRange.from, datesRange.till)}
                    checkDateSelectable={checkDateSelectable}
                  />
                </FormGroup>

              </div>
              <div className="w-auto">
                <Button className="cursor-pointer" onClick={() => removeTrip(index)}>
                  <Icon className="h-6 mr-1" v="trash" /> <span className="hidden md:inline">Удалить поездку</span>
                </Button>
              </div>
            </div>
          )
        )}

        <div className="mt-3 mb-6">
          <Button
            className="btn btn-primary"
            onClick={addTrip}
          >
            Добавить поездку
          </Button>
        </div>

        {hasValidTrips ? <Result
          color={countOfDaysOutsideRussia > 182 ? "negative" : "positive"}
        >
          <div className="pb-4 text-3xl font-bold">
            {countOfDaysOutsideRussia > 182 ? "Вы не налоговый резидент РФ" : "Вы налоговый резидент РФ!"}
          </div>

          <div>За последние 12 месяцев вы провели {countOfDaysOutsideRussia}
            {countOfDaysOutsideRussia > 4 || countOfDaysOutsideRussia == 0 ? " дней " : ""}
            {countOfDaysOutsideRussia === 1 ? " день " : ""}
            {countOfDaysOutsideRussia > 1 && countOfDaysOutsideRussia <= 4 ? " дня " : ""}
            за пределами РФ.
            {countOfDaysOutsideRussia < 183 ? <>
              <br /> Еще {182 - countOfDaysOutsideRussia} дней можно находится вне РФ до потери статуса.
            </> : null}
          </div>


          <p>При расчете дней пребывания дни приезда и дни отъезда из России считаются как полные дни,
            проведенные в России.</p>
        </Result> : null}
      </CalculatorLayout>

      <AdditionalContentLayout>
        <AdditionalInfo>
          <AdditionalInfoSection>
            <H2>Кто является налоговым резидентом РФ?</H2>
            <p>Человек считается налоговым резидентом России тогда, когда его присутствие в стране было как минимум
              183 календарных дня за 12 месяцев подряд.</p>
            <p>Иностранец тоже может быть налоговым резидентом РФ. Гражданство и налоговое резидентство — это не одно и
              то
              же.
              Если гражданин РФ находится на родине менее 183 дней, он перестает быть резидентом. А иностранец, приехав
              к
              нам более чем на 183 дня, становится резидентом РФ со всеми правами.
            </p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>Что означает потеря статуса налогового резидента РФ?</H2>
            <p>Налоговые резиденты платят НДФЛ по ставке 13% (или 15% если их доход более 5 млн
              рублей). Для нерезидентов действует повышенная ставка — 30% (по полученным дивидендам — 15%). Ставка
              распространяется не только на заработную плату, но и на любой полученный доход, в том числе продажу
              машины,
              квартиры и другого имущества.</p>
            <p>НДФЛ налоговым резидентом платится <a
              href="https://www.consultant.ru/document/cons_doc_LAW_28165/62f621e5835790398a88f80270fe2cf0b3710b3c/"
              rel="nofollow noreferrer noopener" target="_blank">не с полной суммы, а с разницы (доход — расход)</a>.
              Нерезидентом — с полной суммы.
            </p>
            <p>Налоговый резидент РФ имеет право на налоговый вычет — частичный возврат расходов при оплате лечения,
              обучения, покупке жилья и прочих жизненных ситуациях, приоритетных для социальной политики государства. У
              нерезидентов такого права нет.
            </p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>Как считаются дни?</H2>
            <ul className="list-ol">
              <li>Считаются 183 дня суммарного пребывания в России в течение последних 12 месяцев;</li>
              <li><a href="https://www.consultant.ru/law/podborki/kak_schitat_183_dnya/" target="_blank"
                     rel="noreferrer">Расчетный
                период может начаться в одном календарном году и продолжаться в следующем;</a></li>
              <li>Учитываются все поездки за период;</li>
              <li>Дни отъезда и возвращения учитываются как пребывание в России.</li>
            </ul>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>Как определяется статус налогового резидента РФ?</H2>
            <p>Федеральная налоговая служба определяет статус налогового резидентства
              ежегодно 31 декабря. С точки зрения налоговой ваш статус на 31 декабря будет распространятся <u
                className="uppercase">на весь
                налоговый период</u>, то есть календарный год.</p>
            <p>Например:</p>
            <ul className="list-ol">
              <li>Если человек в течении календарного года меняет статус с резидента на нерезидента и до конца года
                остается
                в этом статусе, то налог (НДФЛ) за весь год пересчитывается по ставке 30% для нерезидентов;
              </li>
              <li>Если человек с начала года считался нерезидентом и платил 30% НДФЛ, а к концу года сменил статус на
                резидента
                РФ, то налог за весь год в конце года должен быть пересчитан.
              </li>
            </ul>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>Исключения</H2>
            <p>Поездки за границу для лечения или учебы сроком до 6 месяцев не учитываются при определении статуса
              налогового резидента.
              Однако цель поездки необходимо <a href="https://www.klerk.ru/doc/432904/"
                                                rel="nofollow noreferrer noopener"
                                                target="_blank">документально
                подтвердить</a>.
              Чтобы человек оставался резидентом при прохождении терапии или учебы, необходимо предоставить:
            </p>
            <ul className="list-ol">
              <li>Договор и выписку из медицинского учреждения, где было проведено лечение.</li>
              <li>Справку о прохождении учебы.</li>
            </ul>

            <p>Кроме этого не учитывается работа на морских месторождениях углеводородного сырья, а так же военная
              служба
              за
              границей, и командировки госслужащих за рубеж. Эти категории налогоплательщиков остаются налоговыми
              резидентами РФ вне зависимости от срока пребывания в России.</p>
          </AdditionalInfoSection>

          <AdditionalInfoSection>
            <H2>Ссылки</H2>
            <ul className="list-ol">
              <li><a href="https://www.nalog.gov.ru/rn77/fl/interest/dual_nationality/status/" target="_blank"
                     rel="noreferrer">О статусе налогового резидента на сайте ФНС РФ</a></li>
            </ul>
          </AdditionalInfoSection>
        </AdditionalInfo>
      </AdditionalContentLayout>
    </>
  );
}


